import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";
import { SearchableSelect } from "../components/SearchableSelect";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import { useToast } from "../context/ToastContext";
import {
  createZoneGroupApi,
  createZoneGroupCategory,
  deleteZoneGroupApi,
  deleteZoneGroupCategoryApi,
  fetchZoneGroupCategories,
  fetchZoneGroups,
  updateZoneGroupApi,
  updateZoneGroupCategoryApi,
} from "../api/zoneGroupsApi";
import {
  districtDisplayLabel,
  districtSelectOptionsFromStore,
  mandalSelectOptionsFromStore,
  villageSelectOptionsFromStore,
} from "../utils/geoDirectorySelectOptions";
import type { GeoZone, GeoZoneMandalRef, GeoZoneVillageRef, ZoneGroup, ZoneGroupCategory } from "../types";
import { applicationMatchesGeoZone, mandalRefKey, villageRefKey } from "../utils/geoZones";
import { categoryNameById } from "../utils/zoneGroupResolution";

type ZonesTabId = "categories" | "groups" | "zones";

function districtLabel(geoStore: ReturnType<typeof useGeoDirectory>["store"], key: string): string {
  return districtDisplayLabel(geoStore, key);
}

function zoneNameById(zones: GeoZone[], id: string): string {
  return zones.find((z) => z.id === id)?.name ?? id;
}


function ZoneSummaryCard({
  zone,
  zoneIndex,
  isActive,
  onOpen,
}: {
  zone: GeoZone;
  zoneIndex: number;
  isActive: boolean;
  onOpen: () => void;
}) {
  const dCount = zone.districtKeys.length;
  const mCount = zone.mandalRefs.length;
  const vCount = zone.villageRefs.length;
  const stats = [
    dCount ? `${dCount} dist` : "",
    mCount ? `${mCount} mdl` : "",
    vCount ? `${vCount} vil` : "",
  ].filter(Boolean).join(" · ") || "No geo rules";

  return (
    <button
      type="button"
      className={`milestone-master-picker-card${isActive ? " milestone-master-picker-card--active" : ""}`}
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-expanded={isActive}
    >
      <div className="milestone-master-picker-card__content">
        <span className="milestone-master-picker-card__top">
          <span className="milestone-master-picker-card__phase-badge">Zone {zoneIndex + 1}</span>
        </span>
        <span className="milestone-master-picker-card__title">{zone.name}</span>
        <span className="milestone-master-picker-card__stats" aria-label="Coverage stats">
          <span>{stats}</span>
        </span>
      </div>
      <span className="milestone-master-picker-card__footer">
        <span>{isActive ? "Editor open" : "Click card to edit"}</span>
      </span>
    </button>
  );
}

function ZoneEditorCard({ zone: z, index, total, onDeleted }: { zone: GeoZone; index: number; total: number; onDeleted?: () => void }) {
  const { store: geoStore } = useGeoDirectory();
  const { applications, updateGeoZone, removeGeoZone } = useAppState();
  const [name, setName] = useState(z.name);
  const [notes, setNotes] = useState(z.notes);

  useEffect(() => {
    setName(z.name);
    setNotes(z.notes);
  }, [z.id, z.name, z.notes]);

  const matchCount = useMemo(
    () => applications.filter((a) => applicationMatchesGeoZone(a, z)).length,
    [applications, z],
  );

  const [mandalDistrict, setMandalDistrict] = useState("");
  const [mandalName, setMandalName] = useState("");
  const [villageDistrict, setVillageDistrict] = useState("");
  const [villageMandal, setVillageMandal] = useState("");
  const [villageName, setVillageName] = useState("");

  const [scopeType, setScopeType] = useState<"district" | "mandal" | "village" | "mixed">(() => {
    const d = z.districtKeys.length > 0;
    const m = z.mandalRefs.length > 0;
    const v = z.villageRefs.length > 0;
    if (d && !m && !v) return "district";
    if (!d && m && !v) return "mandal";
    if (!d && !m && v) return "village";
    if (!d && !m && !v) return "district";
    return "mixed";
  });

  useEffect(() => {
    if (!geoStore) return;
    const first = geoStore.tgRegistrationDistricts()[0]?.districtKey ?? "";
    setMandalDistrict((prev) => prev || first);
    setVillageDistrict((prev) => prev || first);
  }, [geoStore]);

  const mandalsForPick = useMemo(
    () => (geoStore ? geoStore.tgMandalsForDistrict(mandalDistrict) : []),
    [geoStore, mandalDistrict],
  );
  const villagesForPick = useMemo(() => {
    if (!geoStore || !villageMandal.trim()) return [];
    return geoStore.tgVillagesForMandal(villageDistrict, villageMandal);
  }, [geoStore, villageDistrict, villageMandal]);

  const districtPickOptions = useMemo(() => districtSelectOptionsFromStore(geoStore), [geoStore]);

  const mandalPickOptions = useMemo(() => {
    const sorted = mandalSelectOptionsFromStore(geoStore, mandalDistrict);
    return [{ value: "", label: "— Choose mandal —", rowKey: "none" }, ...sorted];
  }, [geoStore, mandalDistrict]);

  const villageDistrictPickOptions = districtPickOptions;

  const villageMandalPickOptions = useMemo(() => {
    const sorted = mandalSelectOptionsFromStore(geoStore, villageDistrict);
    return [{ value: "", label: "— Choose mandal —", rowKey: "none" }, ...sorted];
  }, [villageDistrict, geoStore]);

  const villageNamePickOptions = useMemo(() => {
    const sorted = villageSelectOptionsFromStore(geoStore, villageDistrict, villageMandal);
    return [{ value: "", label: "— Choose village —", rowKey: "none" }, ...sorted];
  }, [villagesForPick, geoStore, villageDistrict, villageMandal]);

  function toggleDistrict(key: string) {
    const has = z.districtKeys.includes(key);
    const districtKeys = has ? z.districtKeys.filter((k) => k !== key) : [...z.districtKeys, key];
    void updateGeoZone(z.id, { districtKeys });
  }

  function addMandal() {
    const mn = mandalName.trim();
    if (!mn) return;
    const ref: GeoZoneMandalRef = { district: mandalDistrict, mandalName: mn };
    const k = mandalRefKey(ref.district, ref.mandalName);
    if (z.mandalRefs.some((r) => mandalRefKey(r.district, r.mandalName) === k)) return;
    void updateGeoZone(z.id, { mandalRefs: [...z.mandalRefs, ref] });
    setMandalName("");
  }

  function removeMandal(ref: GeoZoneMandalRef) {
    void updateGeoZone(z.id, {
      mandalRefs: z.mandalRefs.filter(
        (r) => mandalRefKey(r.district, r.mandalName) !== mandalRefKey(ref.district, ref.mandalName),
      ),
    });
  }

  function addVillage() {
    const mn = villageMandal.trim();
    const vn = villageName.trim();
    if (!mn || !vn) return;
    const ref: GeoZoneVillageRef = { district: villageDistrict, mandalName: mn, villageName: vn };
    const k = villageRefKey(ref.district, ref.mandalName, ref.villageName);
    if (
      z.villageRefs.some(
        (r) => villageRefKey(r.district, r.mandalName, r.villageName) === k,
      )
    ) {
      return;
    }
    void updateGeoZone(z.id, { villageRefs: [...z.villageRefs, ref] });
    setVillageName("");
  }

  function removeVillage(ref: GeoZoneVillageRef) {
    void updateGeoZone(z.id, {
      villageRefs: z.villageRefs.filter(
        (r) =>
          villageRefKey(r.district, r.mandalName, r.villageName) !==
          villageRefKey(ref.district, ref.mandalName, ref.villageName),
      ),
    });
  }

  return (
    <div className="milestone-master-modal-editor geo-zones-card">
      <div className="toolbar-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <h2 className="card__title card__title--gov" style={{ margin: 0 }}>
          Zone {index + 1} of {total}
        </h2>
        <button
          type="button"
          className="btn btn--tg-secondary btn--sm milestone-row__remove"
          onClick={() => {
            if (window.confirm(`Delete zone “${z.name}”?`)) {
              void (async () => {
                const ok = await removeGeoZone(z.id);
                if (ok) onDeleted?.();
              })();
            }
          }}
        >
          Delete zone
        </button>
      </div>

      <div className="field">
        <label htmlFor={`zone-name-${z.id}`}>Zone name</label>
        <input id={`zone-name-${z.id}`} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
      </div>
      <div className="field">
        <label htmlFor={`zone-notes-${z.id}`}>Notes</label>
        <textarea id={`zone-notes-${z.id}`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
      </div>
      <button
        type="button"
        className="btn btn--primary btn--tg btn--sm"
        onClick={() => void updateGeoZone(z.id, { name, notes })}
      >
        Save name &amp; notes
      </button>

      <p className="geo-zones-match text-muted section-lead section-lead--tight" style={{ marginTop: "1rem" }}>
        <strong>{matchCount}</strong> applicant file{matchCount === 1 ? "" : "s"} in this console match this zone (by district /
        mandal / village rules below).
      </p>

      <h3 className="form-section-title" style={{ marginTop: "1.15rem" }}>
        Coverage type
      </h3>
      <p className="section-lead section-lead--tight text-muted">
        Choose one level to simplify the rules, or choose "Mixed" to combine multiple levels in a single zone.
      </p>
      <div className="field">
        <select
          value={scopeType}
          onChange={(e) => setScopeType(e.target.value as any)}
          className="form-control"
        >
          <option value="district">Entire districts</option>
          <option value="mandal">Specific mandals</option>
          <option value="village">Specific villages</option>
          <option value="mixed">Mixed (Advanced)</option>
        </select>
      </div>

      {scopeType === "district" || scopeType === "mixed" ? (
        <>
          <h3 className="form-section-title" style={{ marginTop: "1.15rem" }}>
            Entire districts
          </h3>
          <p className="section-lead section-lead--tight text-muted">
            Tick districts to include <strong>all</strong> mandals and villages in the IGRS directory for that district.
          </p>
          <div className="geo-zones-district-grid">
            {districtPickOptions.map((d) => (
              <label key={d.value} className="geo-zones-check">
                <input type="checkbox" checked={z.districtKeys.includes(d.value)} onChange={() => toggleDistrict(d.value)} />
                <span>{d.label}</span>
              </label>
            ))}
          </div>
        </>
      ) : null}

      {scopeType === "mandal" || scopeType === "mixed" ? (
        <>
          <h3 className="form-section-title" style={{ marginTop: "1.25rem" }}>
            Specific mandals
          </h3>
          <p className="section-lead section-lead--tight text-muted">
            Add mandals when only part of a district belongs to this zone (names from registration directory).
          </p>
          <div className="geo-zones-add-row">
            <div className="field field-inline">
              <label htmlFor={`zone-${z.id}-mandal-district`}>District</label>
              <SearchableSelect
                id={`zone-${z.id}-mandal-district`}
                variant="dash"
                value={mandalDistrict}
                onChange={(v) => {
                  if (v) {
                    setMandalDistrict(v);
                    setMandalName("");
                  }
                }}
                options={districtPickOptions}
                placeholder="District"
              />
            </div>
            <div className="field field-inline" style={{ flex: "1 1 12rem" }}>
              <label htmlFor={`zone-${z.id}-mandal-name`}>Mandal</label>
              <SearchableSelect
                id={`zone-${z.id}-mandal-name`}
                variant="dash"
                value={mandalName}
                onChange={setMandalName}
                options={mandalPickOptions}
                placeholder="— Choose mandal —"
              />
            </div>
            <button type="button" className="btn btn--tg-secondary btn--sm" disabled={!mandalName.trim()} onClick={addMandal}>
              Add mandal
            </button>
          </div>
          {z.mandalRefs.length > 0 ? (
            <ul className="geo-zones-pill-list">
              {z.mandalRefs.map((r) => (
                <li key={mandalRefKey(r.district, r.mandalName)} className="geo-zones-pill-row">
                  <span>
                    <strong>{districtLabel(geoStore, r.district)}</strong> — {r.mandalName}
                  </span>
                  <button type="button" className="btn btn--link btn--sm" onClick={() => removeMandal(r)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted section-lead section-lead--tight">No mandal-level rows yet.</p>
          )}
        </>
      ) : null}

      {scopeType === "village" || scopeType === "mixed" ? (
        <>
          <h3 className="form-section-title" style={{ marginTop: "1.25rem" }}>
            Specific villages
          </h3>
          <p className="section-lead section-lead--tight text-muted">Add individual villages for the finest control.</p>
          <div className="geo-zones-add-row geo-zones-add-row--wrap">
            <div className="field field-inline">
              <label htmlFor={`zone-${z.id}-village-district`}>District</label>
              <SearchableSelect
                id={`zone-${z.id}-village-district`}
                variant="dash"
                value={villageDistrict}
                onChange={(v) => {
                  if (v) {
                    setVillageDistrict(v);
                    setVillageMandal("");
                    setVillageName("");
                  }
                }}
                options={villageDistrictPickOptions}
                placeholder="District"
              />
            </div>
            <div className="field field-inline" style={{ flex: "1 1 11rem" }}>
              <label htmlFor={`zone-${z.id}-village-mandal`}>Mandal</label>
              <SearchableSelect
                id={`zone-${z.id}-village-mandal`}
                variant="dash"
                value={villageMandal}
                onChange={(v) => {
                  setVillageMandal(v);
                  setVillageName("");
                }}
                options={villageMandalPickOptions}
                placeholder="— Choose mandal —"
              />
            </div>
            <div className="field field-inline" style={{ flex: "1 1 12rem" }}>
              <label htmlFor={`zone-${z.id}-village-name`}>Village</label>
              <SearchableSelect
                id={`zone-${z.id}-village-name`}
                variant="dash"
                value={villageName}
                onChange={setVillageName}
                options={villageNamePickOptions}
                placeholder="— Choose village —"
              />
            </div>
            <button type="button" className="btn btn--tg-secondary btn--sm" disabled={!villageMandal.trim() || !villageName.trim()} onClick={addVillage}>
              Add village
            </button>
          </div>
          {z.villageRefs.length > 0 ? (
            <ul className="geo-zones-pill-list">
              {z.villageRefs.map((r) => (
                <li key={villageRefKey(r.district, r.mandalName, r.villageName)} className="geo-zones-pill-row">
                  <span>
                    <strong>{districtLabel(geoStore, r.district)}</strong> — {r.mandalName} — {r.villageName}
                  </span>
                  <button type="button" className="btn btn--link btn--sm" onClick={() => removeVillage(r)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted section-lead section-lead--tight">No village-level rows yet.</p>
          )}
        </>
      ) : null}
    </div>
  );
}


function CategorySummaryCard({
  category,
  index,
  isActive,
  onOpen,
}: {
  category: ZoneGroupCategory;
  index: number;
  isActive: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className={`milestone-master-picker-card${isActive ? " milestone-master-picker-card--active" : ""}`}
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-expanded={isActive}
    >
      <div className="milestone-master-picker-card__content">
        <span className="milestone-master-picker-card__top">
          <span className="milestone-master-picker-card__phase-badge">Category {index + 1}</span>
        </span>
        <span className="milestone-master-picker-card__title">{category.name}</span>
        <span className="milestone-master-picker-card__stats" aria-label="Notes">
          <span>{category.notes.trim() ? "Has notes" : "No notes"}</span>
        </span>
      </div>
      <span className="milestone-master-picker-card__footer">
        <span>{isActive ? "Editor open" : "Click card to edit"}</span>
      </span>
    </button>
  );
}

function CategoryEditorCard({
  category,
  index,
  total,
  onDeleted,
  onReload,
}: {
  category: ZoneGroupCategory;
  index: number;
  total: number;
  onDeleted?: () => void;
  onReload: () => Promise<void>;
}) {
  const { showToast } = useToast();
  const [name, setName] = useState(category.name);
  const [notes, setNotes] = useState(category.notes);
  const [saving, setSaving] = useState(false);

  return (
    <div className="milestone-master-modal-editor geo-zones-card">
      <div className="toolbar-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <h2 className="card__title card__title--gov" style={{ margin: 0 }}>
          Category {index + 1} of {total}
        </h2>
        <button
          type="button"
          className="btn btn--tg-secondary btn--sm milestone-row__remove"
          onClick={() => {
            if (window.confirm(`Delete category “${category.name}”? All zone groups using this category will be removed.`)) {
              void (async () => {
                try {
                  await deleteZoneGroupCategoryApi(category.id);
                  await onReload();
                  showToast("Category deleted.", "success");
                  onDeleted?.();
                } catch (e) {
                  showToast(e instanceof Error ? e.message : "Could not delete category.", "error");
                }
              })();
            }
          }}
        >
          Delete category
        </button>
      </div>
      <div className="field">
        <label htmlFor={`cat-name-${category.id}`}>Category name</label>
        <input id={`cat-name-${category.id}`} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
      </div>
      <div className="field">
        <label htmlFor={`cat-notes-${category.id}`}>Notes</label>
        <textarea id={`cat-notes-${category.id}`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
      </div>
      <button
        type="button"
        className="btn btn--primary btn--tg btn--sm"
        disabled={saving || !name.trim()}
        onClick={() => {
          void (async () => {
            setSaving(true);
            try {
              await updateZoneGroupCategoryApi(category.id, { name: name.trim(), notes: notes.trim() });
              await onReload();
              showToast("Category updated.", "success");
            } catch (e) {
              showToast(e instanceof Error ? e.message : "Update failed.", "error");
            } finally {
              setSaving(false);
            }
          })();
        }}
      >
        {saving ? "Saving..." : "Save category"}
      </button>
    </div>
  );
}

function GroupSummaryCard({
  group,
  index,
  isActive,
  onOpen,
  geoZonesSorted,
  categories,
}: {
  group: ZoneGroup;
  index: number;
  isActive: boolean;
  onOpen: () => void;
  geoZonesSorted: GeoZone[];
  categories: ZoneGroupCategory[];
}) {
  const zName = zoneNameById(geoZonesSorted, group.zoneId);
  const cName = categoryNameById(categories, group.categoryId);
  return (
    <button
      type="button"
      className={`milestone-master-picker-card${isActive ? " milestone-master-picker-card--active" : ""}`}
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-expanded={isActive}
    >
      <div className="milestone-master-picker-card__content">
        <span className="milestone-master-picker-card__top">
          <span className="milestone-master-picker-card__phase-badge">Group {index + 1}</span>
        </span>
        <span className="milestone-master-picker-card__title">{group.name}</span>
        <span className="milestone-master-picker-card__stats" aria-label="Zone and Category">
          <span>{zName}</span>
          <span className="milestone-master-picker-card__stat-dot" aria-hidden>·</span>
          <span>{cName || "No Category"}</span>
        </span>
      </div>
      <span className="milestone-master-picker-card__footer">
        <span>{isActive ? "Editor open" : "Click card to edit"}</span>
      </span>
    </button>
  );
}

function GroupEditorCard({
  group,
  index,
  total,
  geoZonesSorted,
  categories,
  onDeleted,
  onReload,
}: {
  group: ZoneGroup;
  index: number;
  total: number;
  geoZonesSorted: GeoZone[];
  categories: ZoneGroupCategory[];
  onDeleted?: () => void;
  onReload: () => Promise<void>;
}) {
  const { showToast } = useToast();
  const [name, setName] = useState(group.name);
  const [notes, setNotes] = useState(group.notes);
  const [zoneId, setZoneId] = useState(group.zoneId);
  const [categoryId, setCategoryId] = useState(group.categoryId);
  const [saving, setSaving] = useState(false);

  return (
    <div className="milestone-master-modal-editor geo-zones-card">
      <div className="toolbar-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <h2 className="card__title card__title--gov" style={{ margin: 0 }}>
          Group {index + 1} of {total}
        </h2>
        <button
          type="button"
          className="btn btn--tg-secondary btn--sm milestone-row__remove"
          onClick={() => {
            if (window.confirm(`Delete group “${group.name}”?`)) {
              void (async () => {
                try {
                  await deleteZoneGroupApi(group.id);
                  await onReload();
                  showToast("Group deleted.", "success");
                  onDeleted?.();
                } catch (e) {
                  showToast(e instanceof Error ? e.message : "Could not delete group.", "error");
                }
              })();
            }
          }}
        >
          Delete group
        </button>
      </div>
      <div className="field">
        <label>Zone</label>
        <select className="form-control" value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
          {geoZonesSorted.map((z) => (
            <option key={z.id} value={z.id}>{z.name}</option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Category</label>
        <select className="form-control" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Group name</label>
        <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
      </div>
      <div className="field">
        <label>Notes</label>
        <textarea className="form-control" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
      </div>
      <button
        type="button"
        className="btn btn--primary btn--tg btn--sm"
        disabled={saving || !name.trim() || !zoneId || !categoryId}
        onClick={() => {
          void (async () => {
            setSaving(true);
            try {
              await updateZoneGroupApi(group.id, { name: name.trim(), notes: notes.trim(), zoneId, categoryId });
              await onReload();
              showToast("Group updated.", "success");
            } catch (e) {
              showToast(e instanceof Error ? e.message : "Update failed.", "error");
            } finally {
              setSaving(false);
            }
          })();
        }}
      >
        {saving ? "Saving..." : "Save group"}
      </button>
    </div>
  );
}


export function ZonesPage() {
  const { geoZones, addGeoZone } = useAppState();
  const sortedZones = useMemo(() => [...geoZones].sort((a, b) => a.sortOrder - b.sortOrder), [geoZones]);
  const [activeTab, setActiveTab] = useState<ZonesTabId>("zones");

  // Zone State
  const [createName, setCreateName] = useState("");
  const [createNotes, setCreateNotes] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalZoneId, setEditModalZoneId] = useState<string | null>(null);

  // Category State
  const [catName, setCatName] = useState("");
  const [catNotes, setCatNotes] = useState("");
  const [createCatModalOpen, setCreateCatModalOpen] = useState(false);
  const [editModalCatId, setEditModalCatId] = useState<string | null>(null);

  // Group State
  const [groupName, setGroupName] = useState("");
  const [groupNotes, setGroupNotes] = useState("");
  const [groupZoneId, setGroupZoneId] = useState("");
  const [groupCatId, setGroupCatId] = useState("");
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false);
  const [editModalGroupId, setEditModalGroupId] = useState<string | null>(null);

  const [categories, setCategories] = useState<ZoneGroupCategory[]>([]);
  const [zoneGroupsAll, setZoneGroupsAll] = useState<ZoneGroup[]>([]);
  const [zoneGroupsMysqlReady, setZoneGroupsMysqlReady] = useState(false);
  const { showToast } = useToast();

  const sortedGroups = useMemo(
    () =>
      [...zoneGroupsAll].sort((a, b) => {
        const za = zoneNameById(sortedZones, a.zoneId).localeCompare(zoneNameById(sortedZones, b.zoneId));
        if (za !== 0) return za;
        return a.name.localeCompare(b.name);
      }),
    [zoneGroupsAll, sortedZones],
  );

  const editModalZone = useMemo(() => sortedZones.find((x) => x.id === editModalZoneId), [editModalZoneId, sortedZones]);
  const editModalIndex = editModalZone ? sortedZones.findIndex((z) => z.id === editModalZone.id) : -1;

  const editModalCat = useMemo(() => categories.find((x) => x.id === editModalCatId), [editModalCatId, categories]);
  const editModalCatIndex = editModalCat ? categories.findIndex((z) => z.id === editModalCat.id) : -1;

  const editModalGroup = useMemo(() => sortedGroups.find((x) => x.id === editModalGroupId), [editModalGroupId, sortedGroups]);
  const editModalGroupIndex = editModalGroup ? sortedGroups.findIndex((z) => z.id === editModalGroup.id) : -1;

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setCreateName("");
    setCreateNotes("");
    
    setCreateCatModalOpen(false);
    setCatName("");
    setCatNotes("");

    setCreateGroupModalOpen(false);
    setGroupName("");
    setGroupNotes("");
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeCreateModal();
        setEditModalZoneId(null);
        setEditModalCatId(null);
        setEditModalGroupId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeCreateModal]);

  const openCreateModal = () => {
    closeCreateModal();
    setEditModalZoneId(null);
    setEditModalCatId(null);
    setEditModalGroupId(null);
    if (activeTab === "zones") setCreateModalOpen(true);
    if (activeTab === "categories") setCreateCatModalOpen(true);
    if (activeTab === "groups") {
      setGroupZoneId(sortedZones[0]?.id || "");
      setGroupCatId(categories[0]?.id || "");
      setCreateGroupModalOpen(true);
    }
  };

  const openEditModal = (type: string, id: string) => {
    closeCreateModal();
    setEditModalZoneId(type === "zone" && editModalZoneId !== id ? id : null);
    setEditModalCatId(type === "category" && editModalCatId !== id ? id : null);
    setEditModalGroupId(type === "group" && editModalGroupId !== id ? id : null);
  };

  const reloadZoneGroupSetup = useCallback(async () => {
    try {
      const [cats, grps] = await Promise.all([fetchZoneGroupCategories(), fetchZoneGroups()]);
      setCategories(cats);
      setZoneGroupsAll(grps);
      setZoneGroupsMysqlReady(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setZoneGroupsMysqlReady(false);
      setCategories([]);
      setZoneGroupsAll([]);
      if (!/MySQL|503|not configured|MariaDB/i.test(msg)) {
        console.warn("[zones] zone group setup:", msg);
      }
    }
  }, []);

  useEffect(() => {
    void reloadZoneGroupSetup();
  }, [reloadZoneGroupSetup]);

  const tabBtn = (id: ZonesTabId, label: string) => (
    <button
      key={id}
      type="button"
      role="tab"
      aria-selected={activeTab === id}
      className={`zones-page-tab ${activeTab === id ? "zones-page-tab--active" : ""}`}
      onClick={() => setActiveTab(id)}
    >
      {label}
    </button>
  );

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Zones" }]}
        title={
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            Zones master
            <span className="milestone-master__catalog-count">
              {activeTab === "zones" ? sortedZones.length : activeTab === "categories" ? categories.length : sortedGroups.length}
            </span>
          </span>
        }
        actions={
          <button type="button" className="btn btn--primary btn--tg btn--sm" onClick={openCreateModal}>
            {activeTab === "zones" ? "Add zone" : activeTab === "categories" ? "Add category" : "Add group"}
          </button>
        }
      />

      <div className="zones-page-shell">
        <div className="zones-page-tabs-row">
          <div className="zones-page-tabs-wrap" role="tablist" aria-label="Zones setup steps">
            {tabBtn("zones", "Zone")}
            {tabBtn("categories", "Category")}
            {tabBtn("groups", "Group")}
          </div>
        </div>

        <div className="zones-page-panel">
      {activeTab === "zones" ? (
        <>
          <section className="milestone-master__catalog" style={{ marginTop: "1.75rem" }}>
            {sortedZones.length === 0 ? (
              <div className="milestone-master__empty">
                <p className="milestone-master__empty-title">No zones yet</p>
                <p className="milestone-master__empty-desc">
                  Create your first zone with <strong>Add zone</strong> to define geography.
                </p>
              </div>
            ) : (
              <div className="milestone-master-picker-grid">
                {sortedZones.map((z, i) => (
                  <ZoneSummaryCard
                    key={z.id}
                    zone={z}
                    zoneIndex={i}
                    isActive={editModalZoneId === z.id}
                    onOpen={() => openEditModal("zone", z.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {createModalOpen ? (
            <div className="modal-backdrop" role="presentation" onClick={closeCreateModal}>
              <div className="modal milestone-master-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="milestone-master-modal__header">
                  <h2 className="milestone-master-modal__title">Add zone</h2>
                  <button type="button" className="btn btn--tg-secondary btn--sm" onClick={closeCreateModal}>Close</button>
                </div>
                <div className="milestone-master-modal__body">
                  <div className="field">
                    <label htmlFor="new-zone-name">Zone name</label>
                    <input
                      id="new-zone-name"
                      value={createName}
                      onChange={(e) => setCreateName(e.target.value)}
                      placeholder="e.g. North Telangana cluster"
                      maxLength={200}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="new-zone-notes">Notes (optional)</label>
                    <textarea id="new-zone-notes" rows={2} value={createNotes} onChange={(e) => setCreateNotes(e.target.value)} maxLength={2000} />
                  </div>
                  <div className="toolbar-row" style={{ gap: "0.5rem", marginTop: "0.75rem" }}>
                    <button
                      type="button"
                      className="btn btn--primary btn--tg"
                      disabled={!createName.trim()}
                      onClick={() => {
                        void (async () => {
                          const id = await addGeoZone({ name: createName.trim(), notes: createNotes.trim() });
                          if (!id) return;
                          closeCreateModal();
                        })();
                      }}
                    >
                      Create zone
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {editModalZone && editModalIndex >= 0 ? (
            <div className="modal-backdrop" role="presentation" onClick={() => setEditModalZoneId(null)}>
              <div className="modal milestone-master-modal" style={{ maxWidth: "48rem" }} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="milestone-master-modal__header">
                  <h2 className="milestone-master-modal__title">Edit zone</h2>
                  <button type="button" className="btn btn--tg-secondary btn--sm" onClick={() => setEditModalZoneId(null)}>Close</button>
                </div>
                <div className="milestone-master-modal__body">
                  <ZoneEditorCard
                    key={editModalZone.id}
                    zone={editModalZone}
                    index={editModalIndex}
                    total={sortedZones.length}
                    onDeleted={() => setEditModalZoneId(null)}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </>
) : null}

      
      {activeTab === "categories" ? (
        <>
          <section className="milestone-master__catalog" style={{ marginTop: "1.75rem" }}>
            {!zoneGroupsMysqlReady ? (
               <div className="milestone-master__empty"><p>MySQL is not configured.</p></div>
            ) : categories.length === 0 ? (
              <div className="milestone-master__empty">
                <p className="milestone-master__empty-title">No categories yet</p>
                <p className="milestone-master__empty-desc">
                  Create your first category with <strong>Add category</strong>.
                </p>
              </div>
            ) : (
              <div className="milestone-master-picker-grid">
                {categories.map((c, i) => (
                  <CategorySummaryCard
                    key={c.id}
                    category={c}
                    index={i}
                    isActive={editModalCatId === c.id}
                    onOpen={() => openEditModal("category", c.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {createCatModalOpen ? (
            <div className="modal-backdrop" role="presentation" onClick={closeCreateModal}>
              <div className="modal milestone-master-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="milestone-master-modal__header">
                  <h2 className="milestone-master-modal__title">Add category</h2>
                  <button type="button" className="btn btn--tg-secondary btn--sm" onClick={closeCreateModal}>Close</button>
                </div>
                <div className="milestone-master-modal__body">
                  <div className="field">
                    <label>Category name</label>
                    <input className="form-control" value={catName} onChange={(e) => setCatName(e.target.value)} maxLength={200} />
                  </div>
                  <div className="field">
                    <label>Notes (optional)</label>
                    <textarea className="form-control" rows={2} value={catNotes} onChange={(e) => setCatNotes(e.target.value)} maxLength={2000} />
                  </div>
                  <div className="toolbar-row" style={{ gap: "0.5rem", marginTop: "0.75rem" }}>
                    <button
                      type="button"
                      className="btn btn--primary btn--tg"
                      disabled={!catName.trim()}
                      onClick={() => {
                        void (async () => {
                          try {
                            await createZoneGroupCategory({ name: catName.trim(), notes: catNotes.trim() });
                            await reloadZoneGroupSetup();
                            showToast("Category created.", "success");
                            closeCreateModal();
                          } catch (e) {
                            showToast(e instanceof Error ? e.message : "Creation failed.", "error");
                          }
                        })();
                      }}
                    >
                      Create category
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {editModalCat && editModalCatIndex >= 0 ? (
            <div className="modal-backdrop" role="presentation" onClick={() => setEditModalCatId(null)}>
              <div className="modal milestone-master-modal" style={{ maxWidth: "32rem" }} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="milestone-master-modal__header">
                  <h2 className="milestone-master-modal__title">Edit category</h2>
                  <button type="button" className="btn btn--tg-secondary btn--sm" onClick={() => setEditModalCatId(null)}>Close</button>
                </div>
                <div className="milestone-master-modal__body">
                  <CategoryEditorCard
                    key={editModalCat.id}
                    category={editModalCat}
                    index={editModalCatIndex}
                    total={categories.length}
                    onDeleted={() => setEditModalCatId(null)}
                    onReload={reloadZoneGroupSetup}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {activeTab === "groups" ? (
        <>
          <section className="milestone-master__catalog" style={{ marginTop: "1.75rem" }}>
            {!zoneGroupsMysqlReady ? (
               <div className="milestone-master__empty"><p>MySQL is not configured.</p></div>
            ) : sortedGroups.length === 0 ? (
              <div className="milestone-master__empty">
                <p className="milestone-master__empty-title">No groups yet</p>
                <p className="milestone-master__empty-desc">
                  Create your first group with <strong>Add group</strong>.
                </p>
              </div>
            ) : (
              <div className="milestone-master-picker-grid">
                {sortedGroups.map((g, i) => (
                  <GroupSummaryCard
                    key={g.id}
                    group={g}
                    index={i}
                    isActive={editModalGroupId === g.id}
                    onOpen={() => openEditModal("group", g.id)}
                    geoZonesSorted={sortedZones}
                    categories={categories}
                  />
                ))}
              </div>
            )}
          </section>

          {createGroupModalOpen ? (
            <div className="modal-backdrop" role="presentation" onClick={closeCreateModal}>
              <div className="modal milestone-master-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="milestone-master-modal__header">
                  <h2 className="milestone-master-modal__title">Add group</h2>
                  <button type="button" className="btn btn--tg-secondary btn--sm" onClick={closeCreateModal}>Close</button>
                </div>
                <div className="milestone-master-modal__body">
                  <div className="field">
                    <label>Zone</label>
                    <select className="form-control" value={groupZoneId} onChange={(e) => setGroupZoneId(e.target.value)}>
                      {sortedZones.map((z) => (
                        <option key={z.id} value={z.id}>{z.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label>Category</label>
                    <select className="form-control" value={groupCatId} onChange={(e) => setGroupCatId(e.target.value)}>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label>Group name</label>
                    <input className="form-control" value={groupName} onChange={(e) => setGroupName(e.target.value)} maxLength={200} />
                  </div>
                  <div className="field">
                    <label>Notes (optional)</label>
                    <textarea className="form-control" rows={2} value={groupNotes} onChange={(e) => setGroupNotes(e.target.value)} maxLength={2000} />
                  </div>
                  <div className="toolbar-row" style={{ gap: "0.5rem", marginTop: "0.75rem" }}>
                    <button
                      type="button"
                      className="btn btn--primary btn--tg"
                      disabled={!groupName.trim() || !groupZoneId || !groupCatId}
                      onClick={() => {
                        void (async () => {
                          try {
                            await createZoneGroupApi({ name: groupName.trim(), notes: groupNotes.trim(), zoneId: groupZoneId, categoryId: groupCatId });
                            await reloadZoneGroupSetup();
                            showToast("Group created.", "success");
                            closeCreateModal();
                          } catch (e) {
                            showToast(e instanceof Error ? e.message : "Creation failed.", "error");
                          }
                        })();
                      }}
                    >
                      Create group
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {editModalGroup && editModalGroupIndex >= 0 ? (
            <div className="modal-backdrop" role="presentation" onClick={() => setEditModalGroupId(null)}>
              <div className="modal milestone-master-modal" style={{ maxWidth: "32rem" }} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="milestone-master-modal__header">
                  <h2 className="milestone-master-modal__title">Edit group</h2>
                  <button type="button" className="btn btn--tg-secondary btn--sm" onClick={() => setEditModalGroupId(null)}>Close</button>
                </div>
                <div className="milestone-master-modal__body">
                  <GroupEditorCard
                    key={editModalGroup.id}
                    group={editModalGroup}
                    index={editModalGroupIndex}
                    total={sortedGroups.length}
                    onDeleted={() => setEditModalGroupId(null)}
                    onReload={reloadZoneGroupSetup}
                    geoZonesSorted={sortedZones}
                    categories={categories}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
        </div>
      </div>
    </>
  );
}
